// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the last chat in the Assistant DM, for an empty Konvo panel to show and pick up
// again. It is the newest run of your own messages (each within CHAT_GAP_MS of the next) and
// Konvo's answers to them. Replies are not linked to their question, so an answer is what
// Konvo sent soon after (the first within FIRST_REPLY_MS, the rest within REPLY_GAP_MS of the
// one before, as one turn's messages arrive together). What Konvo sent on its own (a brief,
// or a nudge minutes later) is not part of it.
// Pure, unit tested in lastChat.test.ts.

import * as SnowflakeUtils from '@konvo/snowflake/src/SnowflakeUtils';

export interface ChatMessage {
	id: string;
	author: {id: string};
	timestamp: Date;
}

export interface LastChat<T> {
	/** Your messages in it, oldest first (the first is the question). */
	requestIds: Array<string>;
	question: T;
	/** Konvo's newest answer in it; null when it was not answered. */
	lastReply: T | null;
	/** Messages after the chat that are not part of it (Konvo's own briefs and nudges). */
	laterIds: Array<string>;
	/** When the chat last moved (the newest message in it). */
	at: Date;
}

const CHAT_GAP_MS = 30 * 60_000;
const FIRST_REPLY_MS = 10 * 60_000;
const REPLY_GAP_MS = 90_000;
const MAX_REQUESTS = 6;

export function lastChat<T extends ChatMessage>(
	messages: ReadonlyArray<T>,
	assistantId: string,
	/** Konvo's own messages that never answer a question (briefs). */
	ownInitiative: (message: T) => boolean = () => false,
): LastChat<T> | null {
	const sorted = [...messages].sort((a, b) => SnowflakeUtils.compare(a.id, b.id));
	const lastIndex = sorted.findLastIndex((message) => message.author.id !== assistantId);
	if (lastIndex < 0) return null;
	// Back from your newest message while the chat kept going.
	const requests: Array<T> = [sorted[lastIndex]];
	let previousAt = sorted[lastIndex].timestamp.getTime();
	for (let index = lastIndex - 1; index >= 0 && requests.length < MAX_REQUESTS; index--) {
		const message = sorted[index];
		if (previousAt - message.timestamp.getTime() > CHAT_GAP_MS) break;
		previousAt = message.timestamp.getTime();
		if (message.author.id !== assistantId) requests.unshift(message);
	}
	// Konvo's answers to the newest one.
	let lastReply: T | null = null;
	let end = lastIndex;
	let edgeAt = sorted[lastIndex].timestamp.getTime();
	for (let index = lastIndex + 1; index < sorted.length; index++) {
		const at = sorted[index].timestamp.getTime();
		if (ownInitiative(sorted[index]) || at - edgeAt > (lastReply ? REPLY_GAP_MS : FIRST_REPLY_MS)) break;
		lastReply = sorted[index];
		edgeAt = at;
		end = index;
	}
	// The newest answer to an earlier question, when the newest one has none yet.
	if (!lastReply) {
		const answered = sorted
			.slice(sorted.indexOf(requests[0]), lastIndex)
			.findLast((message) => message.author.id === assistantId && !ownInitiative(message));
		lastReply = answered ?? null;
	}
	return {
		requestIds: requests.map((message) => message.id),
		question: requests[0],
		lastReply,
		laterIds: sorted.slice(end + 1).map((message) => message.id),
		at: sorted[end].timestamp,
	};
}
